import { useMemo } from 'react';

import {
  NodeResourceOffering,
  NodeResourceOfferingWithSoleTenancy,
} from '@cast/types';

import { MetricsBox } from 'components/boxes';
import { useIsSoleTenantOfferingVisible } from 'hooks/useIsSoleTenantOfferingVisible';

import { SummaryItem } from './SummaryItem';

import {
  nodeResourceOfferingLabelMap,
  SOLE_TENANT_OFFERING_COLOR,
} from '../../../../constants/resource-offering';
import { PreparedNode } from '../../../types';
import { aggregateNodeCountByResourceOffering } from '../../../utils';
import { Typography, Stack } from '@cast/design-system';

type Props = {
  nodes: PreparedNode[];
};

const resourceOfferingColors: Record<
  NodeResourceOfferingWithSoleTenancy,
  string
> = {
  [NodeResourceOffering.ON_DEMAND]: 'blue.300',
  [NodeResourceOffering.FALLBACK]: 'yellow.400',
  [NodeResourceOffering.SPOT]: 'indigo.800',
  'sole-tenant': SOLE_TENANT_OFFERING_COLOR,
};

const resourceOfferingTestIds: Record<
  NodeResourceOfferingWithSoleTenancy,
  string
> = {
  [NodeResourceOffering.ON_DEMAND]: 'nodes-summary-on-demand-count',
  [NodeResourceOffering.FALLBACK]: 'nodes-summary-fallback-count',
  [NodeResourceOffering.SPOT]: 'nodes-summary-spot-count',
  'sole-tenant': 'nodes-summary-sole-tenant-count',
};

type ResourceOfferingMetricProps = {
  resourceOffering: NodeResourceOfferingWithSoleTenancy;
  count: number;
};

const ResourceOfferingMetric = ({
  resourceOffering,
  count,
}: ResourceOfferingMetricProps) => {
  return (
    <MetricsBox
      testId={resourceOfferingTestIds[resourceOffering]}
      markerColor={resourceOfferingColors[resourceOffering]}
      title={nodeResourceOfferingLabelMap[resourceOffering]}
      titleProps={{ variant: 'L10R' }}
      primaryValue={count}
      primaryValueProps={{ variant: 'h6' }}
      sx={{ width: 80 }}
    />
  );
};

export const NodesResourceOfferingsBreakdown = ({ nodes }: Props) => {
  const isSoleTenantOfferingVisible = useIsSoleTenantOfferingVisible();
  const nodeCountByResourceOffering = useMemo(
    () => aggregateNodeCountByResourceOffering(nodes),
    [nodes]
  );

  return (
    <SummaryItem testId="nodes-summary-resource-offerings">
      <Stack gap={16}>
        <Stack gap={4} data-testid="nodes-summary-total">
          <Typography variant="L10R">Total nodes</Typography>
          <Typography variant="h4">{nodes.length}</Typography>
        </Stack>
        <Stack
          direction="row"
          gap={16}
          justifyContent="space-between"
          maxWidth={400}
        >
          <ResourceOfferingMetric
            resourceOffering={NodeResourceOffering.ON_DEMAND}
            count={nodeCountByResourceOffering[NodeResourceOffering.ON_DEMAND]}
          />
          {isSoleTenantOfferingVisible && (
            <ResourceOfferingMetric
              resourceOffering="sole-tenant"
              count={nodeCountByResourceOffering['sole-tenant']}
            />
          )}
          <ResourceOfferingMetric
            resourceOffering={NodeResourceOffering.FALLBACK}
            count={nodeCountByResourceOffering[NodeResourceOffering.FALLBACK]}
          />
          <ResourceOfferingMetric
            resourceOffering={NodeResourceOffering.SPOT}
            count={nodeCountByResourceOffering[NodeResourceOffering.SPOT]}
          />
        </Stack>
      </Stack>
    </SummaryItem>
  );
};
